// ── Zoned Time Utilities ──
// Thin wrapper around Temporal (via temporal-polyfill) for the RFC 9557 zoned
// date-time strings used throughout the schedule, e.g.
// "2026-10-14T10:12:00+09:00[Asia/Tokyo]".
//
// Components must never hand-parse zoned strings or do naive HH:mm arithmetic
// (DST edges) — all time math goes through this module. Wrapping the polyfill
// here keeps a future swap to native Temporal trivial.

import { Temporal } from "temporal-polyfill";
import type { ZonedTimeString } from "@poi-planner/shared";

export type { ZonedTimeString };

/** Parse an RFC 9557 zoned string. Throws on malformed input. */
export function parseZoned(value: ZonedTimeString): Temporal.ZonedDateTime {
  return Temporal.ZonedDateTime.from(value);
}

/** Serialize back to the canonical RFC 9557 string form. */
export function toZonedString(zdt: Temporal.ZonedDateTime): ZonedTimeString {
  return zdt.toString();
}

/** Build a zoned string from a date (YYYY-MM-DD), wall time (HH:mm), and zone. */
export function zonedFromDateAndTime(
  date: string,
  time: string,
  zone: string,
): ZonedTimeString {
  return Temporal.PlainDateTime.from(`${date}T${time}`)
    .toZonedDateTime(zone, { disambiguation: "compatible" })
    .toString();
}

export interface WallTimeCandidate {
  value: ZonedTimeString;
  offset: string;
  occurrence: "earlier" | "later";
}

/**
 * Valid instants for a manually entered wall time. A DST gap returns no
 * candidates, an ordinary time returns one, and a repeated time returns both
 * offsets in chronological order.
 */
export function wallTimeCandidates(
  date: string,
  time: string,
  zone: string,
): WallTimeCandidate[] {
  if (!date || !time || !zone) return [];
  const plain = Temporal.PlainDateTime.from(`${date}T${time}`);
  const earlier = plain.toZonedDateTime(zone, { disambiguation: "earlier" });
  const later = plain.toZonedDateTime(zone, { disambiguation: "later" });

  // Gap disambiguation moves to a different wall time in both directions.
  if (
    !earlier.toPlainDateTime().equals(plain) ||
    !later.toPlainDateTime().equals(plain)
  ) {
    return [];
  }

  const candidates: WallTimeCandidate[] = [
    {
      value: earlier.toString(),
      offset: earlier.offset,
      occurrence: "earlier",
    },
  ];
  if (earlier.epochMilliseconds !== later.epochMilliseconds) {
    candidates.push({
      value: later.toString(),
      offset: later.offset,
      occurrence: "later",
    });
  }
  return candidates;
}

/** Resolve a manually entered wall time, defaulting repeats to the earlier offset. */
export function zonedFromWallTime(
  date: string,
  time: string,
  zone: string,
  preferredOffset?: string | null,
): ZonedTimeString | null {
  const candidates = wallTimeCandidates(date, time, zone);
  if (candidates.length === 0) return null;
  return (
    candidates.find((candidate) => candidate.offset === preferredOffset) ??
    candidates[0]
  ).value;
}

/** The date (YYYY-MM-DD) of a zoned time in its own zone. */
export function zonedDate(value: ZonedTimeString): string {
  return parseZoned(value).toPlainDate().toString();
}

/** The wall-clock time (HH:mm) of a zoned time in its own zone. */
export function zonedTimeOfDay(value: ZonedTimeString): string {
  const zdt = parseZoned(value);
  return `${String(zdt.hour).padStart(2, "0")}:${String(zdt.minute).padStart(2, "0")}`;
}

/** Convert an instant to another zone without changing the instant. */
export function zonedInZone(
  value: ZonedTimeString,
  zone: string,
): Temporal.ZonedDateTime {
  return parseZoned(value).withTimeZone(zone);
}

/** Display date of an instant in an explicit zone. */
export function zonedDateInZone(value: ZonedTimeString, zone: string): string {
  return zonedInZone(value, zone).toPlainDate().toString();
}

/** Display wall time of an instant in an explicit zone. */
export function zonedTimeOfDayInZone(
  value: ZonedTimeString,
  zone: string,
): string {
  const zdt = zonedInZone(value, zone);
  return `${String(zdt.hour).padStart(2, "0")}:${String(zdt.minute).padStart(2, "0")}`;
}

function compactZoneLabel(zone: string): string {
  return (zone.split("/").at(-1) ?? zone).replaceAll("_", " ");
}

function compactDate(date: string): string {
  return date.slice(5);
}

function nativeEndpoint(
  value: ZonedTimeString,
  includeDate: boolean,
  includeOffset: boolean,
): string {
  const zoned = parseZoned(value);
  const date = zoned.toPlainDate().toString();
  const time = `${String(zoned.hour).padStart(2, "0")}:${String(zoned.minute).padStart(2, "0")}`;
  return `${includeDate ? `${compactDate(date)} ` : ""}${time} ${compactZoneLabel(zoned.timeZoneId)}${includeOffset ? ` (UTC${zoned.offset})` : ""}`;
}

export interface EventDisplayTimeInfo {
  displayRange: string;
  nativeRange: string | null;
  tooltip: string;
}

/**
 * Primary display-zone range plus an optional native-zone range. This is the
 * single formatter used by Week, Day, and Month event surfaces.
 */
export function eventDisplayTimeInfo(
  startTime: ZonedTimeString,
  endTime: ZonedTimeString,
  displayZone: string,
  options: { clippedTop?: boolean; clippedBottom?: boolean } = {},
): EventDisplayTimeInfo {
  const displayStartDate = zonedDateInZone(startTime, displayZone);
  const displayEndDate = zonedDateInZone(endTime, displayZone);
  const displayStart = options.clippedTop
    ? "00:00"
    : zonedTimeOfDayInZone(startTime, displayZone);
  const displayEnd = options.clippedBottom
    ? "24:00"
    : zonedTimeOfDayInZone(endTime, displayZone);
  const displayRange = `${displayStart} – ${displayEnd}`;

  const nativeStart = parseZoned(startTime);
  const nativeEnd = parseZoned(endTime);
  const showNative =
    nativeStart.timeZoneId !== displayZone ||
    nativeEnd.timeZoneId !== displayZone ||
    nativeStart.timeZoneId !== nativeEnd.timeZoneId ||
    nativeStart.offset !== nativeEnd.offset;
  const nativeOffsetsDiffer = nativeStart.offset !== nativeEnd.offset;
  const nativeDatesDiffer =
    nativeStart.toPlainDate().toString() !== nativeEnd.toPlainDate().toString();
  const nativeRange = showNative
    ? `${nativeEndpoint(startTime, nativeDatesDiffer, nativeOffsetsDiffer)} → ${nativeEndpoint(endTime, nativeDatesDiffer, nativeOffsetsDiffer)}`
    : null;

  const displayDateRange =
    displayStartDate === displayEndDate
      ? displayStartDate
      : `${displayStartDate} → ${displayEndDate}`;
  const tooltip = nativeRange
    ? `Display: ${displayDateRange} ${displayRange} (${displayZone}). Native: ${nativeRange}.`
    : `Display: ${displayDateRange} ${displayRange} (${displayZone}).`;

  return { displayRange, nativeRange, tooltip };
}

/** Compare two zoned times by instant (negative / 0 / positive). */
export function compareZoned(a: ZonedTimeString, b: ZonedTimeString): number {
  return Temporal.ZonedDateTime.compare(parseZoned(a), parseZoned(b));
}

/** Epoch milliseconds of a zoned time (for sorting by instant). */
export function epochMs(value: ZonedTimeString): number {
  return parseZoned(value).epochMilliseconds;
}

/** Add minutes to a zoned time (DST-safe), returning a zoned string. */
export function addMinutes(
  value: ZonedTimeString,
  minutes: number,
): ZonedTimeString {
  return parseZoned(value).add({ minutes }).toString();
}

/** Shift a zoned time by whole calendar days, preserving wall time and zone. */
export function addCalendarDays(
  value: ZonedTimeString,
  days: number,
): ZonedTimeString {
  return parseZoned(value).add({ days }).toString();
}

/** Exact minutes between two zoned times (b − a). */
export function minutesBetween(a: ZonedTimeString, b: ZonedTimeString): number {
  return parseZoned(a)
    .toInstant()
    .until(parseZoned(b).toInstant())
    .total({ unit: "minutes" });
}

/** Replace the wall-clock time (HH:mm) of a zoned time, keeping date and zone. */
export function withTimeOfDay(
  value: ZonedTimeString,
  time: string,
): ZonedTimeString {
  const [hour, minute] = time.split(":").map(Number);
  return parseZoned(value)
    .with({ hour, minute, second: 0, millisecond: 0 })
    .toString();
}

/** Move a zoned time onto another calendar date, keeping wall time and zone. */
export function withDate(
  value: ZonedTimeString,
  date: string,
): ZonedTimeString {
  const target = Temporal.PlainDate.from(date);
  return parseZoned(value)
    .with({ year: target.year, month: target.month, day: target.day })
    .toString();
}

/**
 * Move an interval to a date-only target: preserve the start wall time, use
 * the destination date's effective zone, and preserve exact elapsed duration.
 */
export function moveIntervalToDate(
  startTime: ZonedTimeString,
  endTime: ZonedTimeString,
  date: string,
  zone: string,
  sourceDisplayZone?: string,
): { startTime: ZonedTimeString; endTime: ZonedTimeString } {
  const movedStart = zonedFromDateAndTime(
    date,
    sourceDisplayZone
      ? zonedTimeOfDayInZone(startTime, sourceDisplayZone)
      : zonedTimeOfDay(startTime),
    zone,
  );
  return {
    startTime: movedStart,
    endTime: addMinutes(movedStart, minutesBetween(startTime, endTime)),
  };
}

// ── Plain date helpers (YYYY-MM-DD) ──

/** Add days to a plain date string. */
export function addDaysToDate(date: string, days: number): string {
  return Temporal.PlainDate.from(date).add({ days }).toString();
}

/** Whole days from date a to date b (b − a). */
export function daysBetweenDates(a: string, b: string): number {
  return Temporal.PlainDate.from(a).until(Temporal.PlainDate.from(b)).days;
}

/** First day (YYYY-MM-DD) of the month containing a plain date. */
export function startOfMonth(date: string): string {
  return Temporal.PlainDate.from(date).with({ day: 1 }).toString();
}

/** Number of days in the month containing a plain date. */
export function daysInMonth(date: string): number {
  return Temporal.PlainDate.from(date).daysInMonth;
}

/** ISO day of week (1 = Monday … 7 = Sunday) of a plain date. */
export function isoDayOfWeek(date: string): number {
  return Temporal.PlainDate.from(date).dayOfWeek;
}

/** Whole calendar months from month of a to month of b (b − a). */
export function monthsBetweenDates(a: string, b: string): number {
  const monthA = Temporal.PlainDate.from(a).toPlainYearMonth();
  const monthB = Temporal.PlainDate.from(b).toPlainYearMonth();
  return monthA.until(monthB, { largestUnit: "months" }).months;
}

/** Add whole calendar months to a plain date (clamped day-of-month). */
export function addMonthsToDate(date: string, months: number): string {
  return Temporal.PlainDate.from(date).add({ months }).toString();
}

/** Enumerate dates from start to end inclusive. */
export function enumerateDates(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  let current = Temporal.PlainDate.from(startDate);
  const end = Temporal.PlainDate.from(endDate);
  while (Temporal.PlainDate.compare(current, end) <= 0) {
    dates.push(current.toString());
    current = current.add({ days: 1 });
  }
  return dates;
}
